import React, {useRef, useLayoutEffect} from 'react';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import {cleanChoiceValue, compareByQuestionChoicesOrder, parseSingleChoiceData} from "../../../../utils/ChartsUtils";

am4core.useTheme(am4themes_animated);

function BarChartComponent(props) {
   const chartRef = useRef(null);

   function updateCurrentChartInfo(chart) {
      let newChartInfo = {
         chartText: props.questionTitle,
         numAnswers: props.answersData.total,
         chart: chart
      };
      props.updateChartInfo && props.updateChartInfo(props.chartIndex, newChartInfo);
   }

   function setChartData(chart, answersData) {
      chart.data = parseSingleChoiceData(answersData)
          .sort(compareByQuestionChoicesOrder(props.questionChoices))
          .map(cleanChoiceValue)
          .reverse();
   }

   function setChartSettings(chart) {
      if (props.isMobile) {
         chart.width = am4core.percent(100);
         chart.paddingTop = 20;
         chart.paddingBottom = 20;
         chart.paddingLeft = 30;
         chart.paddingRight = 30;
      } else {
         chart.width = props.chartConfig.width;
         chart.paddingLeft = 100;
         chart.paddingRight = 200;
      }
      chart.height = props.chartConfig.height;

      chart.responsive.enabled = true;
      chart.maskBullets = false;
      chart.seriesContainer.zIndex = 1000;
      chart.numberFormatter.numberFormat = "#.##";
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.lineX.disabled = true;
      chart.cursor.lineY.disabled = true;
      chart.cursor.behavior = "none";
   }

   function setColumnsSettings(chart) {
      let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "choiceLabel";
      categoryAxis.dataFields.data = "choiceLabelWithCount";
      categoryAxis.renderer.labels.template.wrap = !props.isMobile;
      categoryAxis.renderer.labels.template.textAlign = "end";
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.grid.template.strokeOpacity = 0;
      categoryAxis.renderer.labels.template.dx = -20;
      categoryAxis.renderer.minWidth = 120;
      categoryAxis.renderer.minGridDistance = 0;
      categoryAxis.cursorTooltipEnabled = false;

      categoryAxis.renderer.labels.template.maxWidth = 250;

      if (props.isMobile) {
         categoryAxis.renderer.inside = true;
         categoryAxis.renderer.minWidth = 0;
         categoryAxis.renderer.labels.template.dx = 5;
         categoryAxis.renderer.cellStartLocation = 0;
         categoryAxis.renderer.cellEndLocation = 1;

         // Category Axis Label Text
         categoryAxis.renderer.labels.template.adapter.add("text", function (text) {
            return "{data}";
         });

         // Category Axis Label position on Mobile
         categoryAxis.renderer.labels.template.adapter.add("dy", function (dy) {
            const numberOfBars = chartRef.current.data.length;
            const chartHeight = chartRef.current.height;
            const titleYOffset = (chartHeight/numberOfBars) * (-0.35);
            return titleYOffset;
         });
      }

      let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      valueAxis.cursorTooltipEnabled = false;
      valueAxis.renderer.grid.template.disabled = true;
      valueAxis.min = 0;
      valueAxis.max = props.chartConfig.scale || 100;
      valueAxis.calculateTotals = true;
      valueAxis.strictMinMax = true;

      valueAxis.renderer.labels.template.adapter.add("text", function(text) {
         return text + "%";
      });

      let series = chart.series.push(new am4charts.ColumnSeries);
      series.dataFields.valueX = "noOfAnswers";
      series.dataFields.valueXShow = "totalPercent";
      series.dataFields.categoryY = "choiceLabel";
      series.tooltipText = "[bold]{valueX.totalPercent}%({valueX})";
      series.tooltip.pointerOrientation = "vertical";
      series.calculatePercent = true;
      series.stacked = true;
      let completingSeries = chart.series.push(new am4charts.ColumnSeries);
      completingSeries.dataFields.valueX = "remaining";
      completingSeries.dataFields.valueXShow = "totalPercent";
      completingSeries.dataFields.categoryY = "choiceLabel";
      completingSeries.stacked = true;
      completingSeries.calculatePercent = true;
	  
      if(props.isMobile) {
         let categoryLabel = series.bullets.push(new am4charts.LabelBullet());
         categoryLabel.label.text = "[bold] {valueX.totalPercent}%";
         categoryLabel.label.fontSize = 12;
         categoryLabel.label.fontFamily = "Montserrat, sans-serif";
         categoryLabel.label.truncate = false;
         categoryLabel.label.hideOversized = false;
		 categoryLabel.label.horizontalCenter = "left";
		 categoryLabel.locationX = 1;
		 categoryLabel.dx = 3;
		 chart.maskBullets = false;
         categoryLabel.label.valign = "middle";
         categoryLabel.label.fill = am4core.color("#000");
		 series.bulletsContainer.parent = chart.seriesContainer;
      }

      let columnTemplate = series.columns.template;
      columnTemplate.height = am4core.percent(50);
      columnTemplate.maxHeight = 50;
      columnTemplate.column.cornerRadius(0, 60, 0, 60);
      columnTemplate.strokeOpacity = 0;
      columnTemplate.background.fill = am4core.color("#F7F7F7");
      columnTemplate.fill = am4core.color("#D2B7F9");

      //Series necessary for filling the remaing space of the bar until completion
      let columnRemainingTemplate = completingSeries.columns.template;
      columnRemainingTemplate.height = am4core.percent(50);
      columnRemainingTemplate.maxHeight = 50;
      columnRemainingTemplate.maxWidth = 50;
      columnRemainingTemplate.column.cornerRadius(10, 10, 10, 10);
      columnRemainingTemplate.strokeOpacity = 0;
      columnRemainingTemplate.background.fill = am4core.color("#F7F7F7");
      columnRemainingTemplate.fill = am4core.color("#F7F7F7");

      if(!props.isMobile) {
         // Needed to add a visible part above chartContainer so the labels are visible
         var invisibleContainer = chart.chartContainer.createChild(am4core.Container);
         invisibleContainer.layout = "static";
         invisibleContainer.toBack();
         invisibleContainer.paddingTop = 50;

         var axisTitle = chart.plotContainer.createChild(am4core.Label);
         axisTitle.id = "text_choices_label";
         axisTitle.text = "VALUE";
         axisTitle.fontWeight = 200;
         axisTitle.dx = -80;
         axisTitle.dy = -50;
         axisTitle.zIndex = "5000";

         var dateTitle = chart.plotContainer.createChild(am4core.Label);
         dateTitle.id = "percent_responses_label";
         dateTitle.text = "PERCENT (RESPONSES)";
         dateTitle.fontWeight = 200;
         dateTitle.dy = -50;
         dateTitle.zIndex = "5000";

         var valueLabel = completingSeries.bullets.push(new am4charts.LabelBullet());
         valueLabel.label.fontSize = 15;
         valueLabel.label.truncate = false;
         valueLabel.label.hideOversized = false;
         valueLabel.label.dx = 50;
         valueLabel.label.horizontalCenter = "left";
         valueLabel.label.adapter.add("text", function (text, target) {
            let correspondingDataItem = chart.data.find(e => e.choiceLabel === target.dataItem.categories.categoryY);
            let percent = ((correspondingDataItem.noOfAnswers / props.answersData['total']) * 100).toFixed(2);
            return percent + "%" + " (" + correspondingDataItem.noOfAnswers + ")";
         });
         valueLabel.adapter.add("x", function (x) {
            // Align PERCENT(RESPONSES) with the "valueLabel" LabelBullet
            let label = chartRef.current.plotContainer.children.values.find((elem) => {
               return elem.id === "percent_responses_label"
            });
            label.x = x * (chart.xAxes.values[0].max / 100);
            return x * (chart.xAxes.values[0].max / 100);
         });
      }
   }

   useLayoutEffect(() => {
      let x = am4core.create("chartdiv_" + props.questionId, am4charts.XYChart);

      setChartSettings(x);
      setColumnsSettings(x);
      setChartData(x, props.answersData);

      chartRef.current = x;
      updateCurrentChartInfo(chartRef.current);
      return () => {
         x.dispose();
      };
   }, []);

   useLayoutEffect(() => {
       if(props.isMobile) {
           chartRef.current.width = am4core.percent(100);
       } else {
           chartRef.current.width = props.chartConfig.width;
       }
      chartRef.current.height = props.chartConfig.height;
      chartRef.current.xAxes.values[0].max = props.chartConfig.scale;
      chartRef.current.yAxes.values[0].renderer.labels.template.maxWidth = props.chartConfig.marginLeft + 250;
      updateCurrentChartInfo(chartRef.current);
   }, [props.chartConfig]);

   const chartDivStyle = {
      width: props.isMobile ? "auto" : props.chartConfig.width,
      height: props.chartConfig.height
   };

   useLayoutEffect( () => {
      if(props.sortingOptions !== null) {
         sortData(props.sortingOptions.option);
      }
      updateCurrentChartInfo(chartRef.current);
   },[props.sortingOptions]);


   function sortData(operation){

      if(operation === 'asc'){
         chartRef.current.data = chartRef.current.data.sort(function (a, b) {
            return b.noOfAnswers - a.noOfAnswers;
         });
      }

      if(operation === 'desc'){
         chartRef.current.data = chartRef.current.data.sort(function (a, b) {
            return a.noOfAnswers - b.noOfAnswers;
         });
      }


      if(operation === 'revert'){
         chartRef.current.data = parseSingleChoiceData(props.answersData)
             .sort(compareByQuestionChoicesOrder(props.questionChoices))
             .map(cleanChoiceValue)
             .reverse();
      }
   }

   return (
       <div className="centered-chart" id={"chartdiv_" + props.questionId} style={chartDivStyle}></div>
   );
}

export default BarChartComponent;